import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trash2, Calendar, Coffee, Bell, BellOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { request } from '../../services/cloudApi';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';

const NotificationDropdown = ({ isOpen, onClose, positionClass = 'top-full right-0 mt-2' }) => {
  const { t, i18n } = useTranslation('shared');
  const dateLocale = getDateFnsLocale(i18n.language);
  const {
    notifications, markAsRead, removeNotification, clearAllNotifications, unreadCount,
    adminBellNotifications, removeAdminBellNotification, markAdminBellAsRead,
  } = useNotification();
  const { authToken } = useAuth();
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) return;
      const notificationContainer = document.getElementById('notification-container');
      if (notificationContainer?.contains(event.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'calendar': return <Calendar className="w-4 h-4" />;
      case 'break':    return <Coffee className="w-4 h-4" />;
      default:         return <Bell className="w-4 h-4" />;
    }
  };

  const getIconStyle = (type) => {
    switch (type) {
      case 'calendar': return 'bg-[#2563eb]/10 text-[#3d8ef0] border border-[#3d8ef0]/20';
      case 'break':    return 'bg-orange-500/10 text-orange-400 border border-orange-500/20';
      default:         return 'bg-[#161f31] text-[var(--color-text-muted)] border border-white/10';
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={dropdownRef}
          initial={{ opacity: 0, y: -8, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.97 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className={`absolute ${positionClass} w-96 z-[10000] overflow-hidden
            bg-[var(--color-bg-base)] border border-white/[0.07]
            rounded-2xl shadow-[0_16px_48px_rgba(0,0,0,0.6)]`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.07]">
            <div className="flex items-center gap-2.5">
              <h3 className="text-[15px] font-semibold text-[var(--color-text-primary)]">{t('notificationDropdown.title')}</h3>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 bg-[#2563eb]/15 text-[#3d8ef0] text-xs font-semibold rounded-full border border-[#3d8ef0]/25">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {notifications.length > 0 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={clearAllNotifications}
                  title={t('notificationDropdown.clearAll')}
                  className="p-1.5 rounded-lg text-[var(--color-text-muted)] hover:text-red-400 hover:bg-red-500/10 transition-all duration-150"
                >
                  <Trash2 className="w-4 h-4" />
                </motion.button>
              )}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                title={t('notificationDropdown.close')}
                className="p-1.5 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-white/[0.05] transition-all duration-150"
              >
                <X className="w-4 h-4" />
              </motion.button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-[480px] overflow-y-auto">
            {adminBellNotifications.length === 0 && notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-14 px-4">
                <div className="w-14 h-14 rounded-2xl bg-[#161f31] border border-white/10 flex items-center justify-center mb-4">
                  <BellOff className="w-6 h-6 text-[var(--color-text-muted)]" />
                </div>
                <p className="text-[var(--color-text-secondary)] text-sm font-medium">{t('notificationDropdown.empty')}</p>
                <p className="text-[var(--color-text-muted)] text-xs mt-1 text-center">{t('notificationDropdown.emptyHint')}</p>
              </div>
            ) : (
              <div className="divide-y divide-white/[0.06]">
                {/* Admin Çan Bildirimleri — üstte, toplu silmeden etkilenmez */}
                {adminBellNotifications.map((notif) => (
                  <motion.div
                    key={notif.id}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 12 }}
                    onClick={() => { if (!notif.read) markAdminBellAsRead(notif.id); }}
                    className={`px-4 py-3.5 cursor-pointer transition-colors duration-150 hover:bg-white/[0.04] ${
                      !notif.read ? 'bg-[#3d8ef0]/[0.04]' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* İkon */}
                      <div className="flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center mt-0.5 bg-[#3d8ef0]/10 text-[#3d8ef0] border border-[#3d8ef0]/20">
                        <Bell className="w-4 h-4" />
                      </div>
                      {/* İçerik */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <span className="text-[10px] font-semibold text-[#3d8ef0] uppercase tracking-wider">
                            {t('notificationDropdown.systemMessage')}
                          </span>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {!notif.read && (
                              <div className="w-2 h-2 rounded-full bg-[#3d8ef0]" />
                            )}
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={(e) => { e.stopPropagation(); removeAdminBellNotification(notif.id); }}
                              title={t('notificationDropdown.delete')}
                              className="p-1 rounded-lg text-[var(--color-text-muted)] hover:text-red-400 hover:bg-red-500/10 transition-all duration-150"
                            >
                              <X className="w-3 h-3" />
                            </motion.button>
                          </div>
                        </div>
                        {/* HTML içerik — <a> tıklamaları yeni sekmede açılır + istatistik kaydedilir */}
                        <div
                          className="text-sm text-[var(--color-text-secondary)] leading-5 mb-2 [&_b]:font-semibold [&_b]:text-[var(--color-text-primary)] [&_i]:italic [&_a]:text-[#3d8ef0] [&_a]:underline [&_a]:underline-offset-2 [&_a]:cursor-pointer"
                          dangerouslySetInnerHTML={{ __html: notif.content || '' }}
                          onClick={(e) => {
                            if (e.target.tagName === 'A') {
                              e.preventDefault();
                              e.stopPropagation();
                              const href = e.target.getAttribute('href');
                              if (href) {
                                const url = /^https?:\/\//i.test(href) ? href : `https://${href}`;
                                window.open(url, '_blank', 'noopener,noreferrer');
                                request(`/admin/notifications/${notif.id}/clicked`, { method: 'POST', token: authToken }).catch(() => {});
                              }
                            }
                          }}
                        />
                        <p className="text-[10px] text-[var(--color-text-muted)]">
                          {format(new Date(notif.timestamp), 'dd MMM yyyy, HH:mm', { locale: dateLocale })}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}

                {/* Normal bildirimler */}
                {notifications.map((notification) => (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 12 }}
                    onClick={() => { if (!notification.read) markAsRead(notification.id); }}
                    className={`px-5 py-3.5 cursor-pointer transition-colors duration-150 hover:bg-white/[0.04] ${
                      !notification.read ? 'bg-[#2563eb]/[0.04]' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center mt-0.5 ${getIconStyle(notification.type)}`}>
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-0.5">
                          <h4 className="text-sm font-semibold text-[var(--color-text-primary)] truncate leading-5">
                            {notification.title}
                          </h4>
                          {!notification.read && (
                            <div className="w-2 h-2 rounded-full bg-[#2563eb] flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                        <p className="text-xs text-[var(--color-text-secondary)] mb-2 line-clamp-2 leading-4">
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] text-[var(--color-text-muted)]">
                            {format(new Date(notification.timestamp), 'dd MMM yyyy, HH:mm', { locale: dateLocale })}
                          </p>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => { e.stopPropagation(); removeNotification(notification.id); }}
                            title={t('notificationDropdown.delete')}
                            className="p-1 rounded-lg text-[var(--color-text-muted)] hover:text-red-400 hover:bg-red-500/10 transition-all duration-150"
                          >
                            <X className="w-3 h-3" />
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NotificationDropdown;
